// AutoStations -- live status of the XS-AutoStation units (the on-site
// Dell Micro PCs bridging Passport POS -> XSite Back Office). Pulls the
// TeamViewer device list (online/offline) via /api/teamviewer/devices and
// offers a one-click Connect (TeamViewer deep link) + Copy ID per unit.
// Connecting still authenticates in TeamViewer itself.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function Tile({ label, value, tone }) {
  return (
    <div style={{ flex: '1 1 130px', minWidth: 130, padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface)' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: tone || 'var(--color-text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>{label}</div>
    </div>
  );
}

export default function AutoStations() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  async function load({ initial = false } = {}) {
    if (initial) setLoading(true);
    setError(null);
    try {
      const r = await axios.get('/api/teamviewer/devices');
      setDevices(r.data.devices || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      if (initial) setLoading(false);
    }
  }

  useEffect(() => { load({ initial: true }); }, []);

  const summary = useMemo(() => ({
    total: devices.length,
    online: devices.filter((d) => d.online).length,
    offline: devices.filter((d) => !d.online).length,
  }), [devices]);

  function copyId(id) {
    try { navigator.clipboard?.writeText(String(id)); } catch (_) {}
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  }

  if (loading) return <section className="page"><h2>AutoStations</h2><p>Loading…</p></section>;

  return (
    <section className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        <div>
          <h2 style={{ margin: 0 }}>AutoStations</h2>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
            Live status of the on-site AutoStation units (Passport POS → XSite Back Office). Status from TeamViewer. Connect opens TeamViewer to that unit.
          </div>
        </div>
        <button className="btn-secondary btn-sm" onClick={() => load()}>Refresh</button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', border: '1px solid var(--color-bad)', borderRadius: 8, background: '#fef2f2', color: '#b91c1c', marginBottom: 'var(--space-3)', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <Tile label="Online" value={summary.online} tone="#166534" />
        <Tile label="Offline" value={summary.offline} tone="#b91c1c" />
        <Tile label="Total units" value={summary.total} />
      </div>

      <div style={{ overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: 'var(--color-surface-sunken, #f8fafc)' }}>
            <tr>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>AutoStation</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>TeamViewer ID</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}></th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.teamviewer_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 10px' }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, background: d.online ? '#16a34a' : '#9ca3af' }} />
                  {d.online ? 'Online' : 'Offline'}
                </td>
                <td style={{ padding: '6px 10px' }}>{d.alias || '—'}</td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{d.teamviewer_id}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <a
                    className="btn-primary btn-sm"
                    href={`teamviewer10://control?device=${d.teamviewer_id}`}
                    style={{ textDecoration: 'none', marginRight: 6 }}
                    title="Open TeamViewer and connect to this unit"
                  >
                    Connect
                  </a>
                  <button className="btn-secondary btn-sm" onClick={() => copyId(d.teamviewer_id)}>
                    {copied === d.teamviewer_id ? 'Copied' : 'Copy ID'}
                  </button>
                </td>
              </tr>
            ))}
            {devices.length === 0 && !error && (
              <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-dim)' }}>No AutoStation units found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
